'use client';

import { useEffect, useRef } from 'react';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { getSupabaseClient } from '../database/supabaseClient';

/**
 * Base type for database records - must be objects with string keys
 */
type DatabaseRecord = Record<string, unknown>;

/**
 * Callback types for different database operations
 */
export interface RealtimeCallbacks<T extends DatabaseRecord> {
  onInsert?: (payload: RealtimePostgresChangesPayload<T>) => void;
  onUpdate?: (payload: RealtimePostgresChangesPayload<T>) => void;
  onDelete?: (payload: RealtimePostgresChangesPayload<T>) => void;
}

/**
 * Options for real-time subscription
 */
export interface RealtimeSubscriptionOptions<T extends DatabaseRecord> {
  table: string;
  schema?: string;
  filter?: string;
  callbacks: RealtimeCallbacks<T>;
}

/**
 * Custom hook for subscribing to real-time changes in a Supabase table
 * 
 * Automatically handles:
 * - Channel creation and subscription
 * - Event listeners for INSERT, UPDATE, DELETE
 * - Cleanup on unmount
 * 
 * @param options Configuration for the subscription
 * @returns void
 * 
 * @example
 * ```typescript
 * useRealtimeSubscription({
 *   table: 'customers',
 *   callbacks: {
 *     onInsert: (payload) => {
 *       console.log('New customer:', payload.new);
 *       setCustomers(prev => [...prev, transformCustomer(payload.new)]);
 *     },
 *     onUpdate: (payload) => {
 *       console.log('Updated customer:', payload.new);
 *       setCustomers(prev => prev.map(c => 
 *         c.id === payload.new.id ? transformCustomer(payload.new) : c
 *       ));
 *     },
 *     onDelete: (payload) => {
 *       console.log('Deleted customer:', payload.old);
 *       setCustomers(prev => prev.filter(c => c.id !== payload.old.id));
 *     }
 *   }
 * });
 * ```
 */
export function useRealtimeSubscription<T extends DatabaseRecord = DatabaseRecord>(
  options: RealtimeSubscriptionOptions<T>
): void {
  const { table, schema = 'public', filter, callbacks } = options;
  const channelRef = useRef<RealtimeChannel | null>(null);
  const callbacksRef = useRef(callbacks);
  const supabase = getSupabaseClient();

  // Update callbacks ref when they change, but don't trigger re-subscription
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    // Create a unique channel name
    const channelName = `realtime:${table}:${Date.now()}`;
    
    // Create the channel
    const channel = supabase.channel(channelName);

    // Build the postgres changes config
    const changesConfig = {
      event: '*' as const,
      schema,
      table,
      filter,
    };

    // Subscribe to postgres changes
    channel.on(
      'postgres_changes' as const,
      changesConfig,
      (payload: RealtimePostgresChangesPayload<T>) => {
        // Use ref to get latest callbacks without re-subscribing
        switch (payload.eventType) {
          case 'INSERT':
            callbacksRef.current.onInsert?.(payload);
            break;
          case 'UPDATE':
            callbacksRef.current.onUpdate?.(payload);
            break;
          case 'DELETE':
            callbacksRef.current.onDelete?.(payload);
            break;
        }
      }
    );

    // Subscribe to the channel
    channel.subscribe();

    channelRef.current = channel;

    // Cleanup function
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [table, schema, filter, supabase]);
}
